import { useQuery } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { useTranslation } from "react-i18next";

import { organicJobDeliveryQueryKeys } from "../api/query-keys";

import type { IntegrationDataHook } from "@/features/integrations/shared/renderer/hooks/types";
import type { ModuleDataValue } from "@/features/integrations/shared/renderer/hooks/types";
import { instance } from "@/lib/axios/instance";

type OrganicVacanciesResponse = {
  data: unknown[];
  totalRecords: number;
  totalVacanciesCount?: number;
  segmentVacanciesCount?: number | null;
  activeVacanciesCount?: number;
};

/**
 * Hook that fetches vacancy counts for the organic module client detail metric cards.
 * Calls the vacancies endpoint (same data source as the table) so counts match.
 */
export const useOrganicClientOverviewData: IntegrationDataHook = (params) => {
  const { integrationName, clientId, tenantId } = params;
  const { t } = useTranslation();

  return useQuery<OrganicVacanciesResponse, AxiosError, Record<string, ModuleDataValue>>({
    queryKey: organicJobDeliveryQueryKeys.vacanciesList({
      integrationName,
      clientId: clientId ?? "",
      tenantId: tenantId ?? "",
      page: 1,
      pageSize: 1,
    }),
    queryFn: () =>
      instance
        .get<OrganicVacanciesResponse>(
          `/api/v2/Integrations/${encodeURIComponent(integrationName)}/organic/vacancies`,
          {
            params: {
              clientId,
              tenantId,
              page: 1,
              pageSize: 1,
            },
          }
        )
        .then((res) => res.data),
    select: (data) => {
      const total = data.totalVacanciesCount ?? 0;
      const segment = data.segmentVacanciesCount ?? null;
      const hasSegment = segment !== null;
      return {
        totalVacanciesCount: total,
        segmentVacanciesCount: segment,
        activeVacanciesCount: data.activeVacanciesCount ?? 0,
        activeVacanciesSubtitle: hasSegment
          ? t(`integrations.${integrationName}.organic.clientDetail.activeVacanciesSubtitleSegment`, { count: segment })
          : t(`integrations.${integrationName}.organic.clientDetail.activeVacanciesSubtitleTotal`, { count: total }),
      };
    },
    enabled: !!(tenantId && integrationName && clientId),
    refetchOnReconnect: false,
    staleTime: 20 * 60 * 1000,
  });
};
